/**
 * Authentication store using Zustand.
 */

import { create } from 'zustand';

import { login as apiLogin, logout as apiLogout, getCurrentUser } from '@/api/auth';
import type { UserDetail, LoginRequest } from '@/types';

interface AuthState {
  user: UserDetail | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  fetchUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiLogin(credentials);
      if (response.user) {
        set({
          user: response.user as UserDetail,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        // Fetch full user details
        await get().fetchUser();
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError?.response?.data?.message ||
        (error instanceof Error ? error.message : '登录失败');
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: () => {
    apiLogout();
    set({ user: null, isAuthenticated: false, error: null });
  },

  fetchUser: async () => {
    set({ isLoading: true });
    try {
      const user = await getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
